import * as LDClient from 'launchdarkly-js-client-sdk';
import { useCallback } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { FeatureFlag, FeatureFlagValue } from './feature-flags';

export type LDClientTypes = {
    context: LDClient.LDContext;
    envKey: string;
};

const FEATURE_FLAGS_LOCAL_STORAGE_KEY = 'featureFlags';

const dispatchLocalStorageUpdatedEvent = (key: string) => {
    window.dispatchEvent(
        new StorageEvent('local-storage', {
            key,
        }),
    );
};

const mergeStoredFlags = (flagsToAdd: Record<string, unknown>) => {
    // Used for `change` events, which only carry the flags that changed.
    // Merge the delta on top of the existing flags so unchanged flags are kept.
    let storedFlags: Record<string, unknown> = {};
    try {
        storedFlags = JSON.parse(
            localStorage.getItem(FEATURE_FLAGS_LOCAL_STORAGE_KEY) || '{}',
        );
    } catch {
        // parsing errors will fall back to the empty case
        storedFlags = {};
    }

    localStorage.setItem(
        FEATURE_FLAGS_LOCAL_STORAGE_KEY,
        JSON.stringify({ ...storedFlags, ...flagsToAdd }),
    );
    dispatchLocalStorageUpdatedEvent(FEATURE_FLAGS_LOCAL_STORAGE_KEY);
};

const overwriteStoredFlags = (flags: Record<string, unknown>) => {
    // Used for the initial load. `allFlags()` returns the complete set of
    // client-side flags for the environment, so we replace the stored set.
    localStorage.setItem(
        FEATURE_FLAGS_LOCAL_STORAGE_KEY,
        JSON.stringify(flags),
    );
    dispatchLocalStorageUpdatedEvent(FEATURE_FLAGS_LOCAL_STORAGE_KEY);
};

let currentClient: LDClient.LDClient | undefined;
let initializationChain: Promise<LDClient.LDClient | undefined> =
    Promise.resolve(undefined);

export const LaunchDarklyClientSide = async ({
    envKey,
    context,
}: LDClientTypes): Promise<LDClient.LDClient> => {
    // Serialize initialization so concurrent calls don't race on the tracked
    // client reference, and so a previous client is fully torn down before the
    // next one starts emitting `change` events into the shared localStorage key.
    const nextInitialization = initializationChain
        .catch(() => undefined)
        .then(() => createAndTrackClient({ envKey, context }));

    initializationChain = nextInitialization;

    return nextInitialization;
};

const createAndTrackClient = async ({
    envKey,
    context,
}: LDClientTypes): Promise<LDClient.LDClient> => {
    // Close the previous client first so only one client is ever subscribed to
    // `change` events and writing to localStorage at a time. close() flushes any
    // pending analytics events and removes the instance's listeners and timers.
    if (currentClient) {
        try {
            await currentClient.close();
        } catch {
            // Ignore teardown failures; we still want to create the new client.
        }
        currentClient = undefined;
    }

    const client = LDClient.initialize(envKey, context, {
        disableSyncEventPost: true,
        streaming: false,
        sendEvents: true,
        fetchGoals: false,
    });
    currentClient = client;

    return client.waitUntilReady().then(() => {
        overwriteStoredFlags(client.allFlags());

        client.on('change', (changeSet: LDClient.LDFlagChangeset) => {
            const flagSet: LDClient.LDFlagSet = {};
            for (const key in changeSet) {
                flagSet[key] = changeSet[key].current;
            }

            mergeStoredFlags(flagSet);
        });

        return client;
    });
};

export function useFeatureFlag<T extends FeatureFlagValue = boolean>(
    flag?: FeatureFlag,
): T {
    const [featureFlags] = useLocalStorage<Record<string, FeatureFlagValue>>(
        FEATURE_FLAGS_LOCAL_STORAGE_KEY,
        {},
    );

    const getFlagValueOrDefault = useCallback(() => {
        if (!flag) return false as T;
        return featureFlags[flag.key] ?? flag.defaultValue;
    }, [featureFlags, flag]);

    return getFlagValueOrDefault() as T;
}
